import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { track } from "@/lib/analytics";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Check, Sparkles, Zap, Users, MessagesSquare, Mic, ChevronRight, Loader2, Settings } from "lucide-react";

const FEATURES_PRO = [
  { icon: Zap, text: "Unlimited AI interaction analyses" },
  { icon: MessagesSquare, text: "Unlimited Ask Harmonix chat" },
  { icon: Mic, text: "Voice chat (hands-free)" },
  { icon: Sparkles, text: "Symptom-pattern insights" },
  { icon: Users, text: "Unlimited family profiles" },
];

const FEATURES_FREE = [
  "1 AI analysis per month",
  "Medication + supplement tracker",
  "Symptom journal",
  "Doctor share links",
  "1 profile",
];

export default function Pricing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [cadence, setCadence] = useState("monthly");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [p, mp] = await Promise.all([
          api.get("/plans"),
          user ? api.get("/me/plan") : Promise.resolve({ data: null }),
        ]);
        setPlans(p.data.plans || []);
        setCurrentPlan(mp.data);
      } catch (_e) {}
    })();
  }, [user]);

  const selected = plans.find((p) => p.lookup_key === `harmonix_pro_${cadence}`);
  const monthlyEquivalent = cadence === "yearly" && selected ? (selected.amount / 12 / 100).toFixed(2) : null;

  const startCheckout = async () => {
    if (!user) {
      track("Upgrade Signup Redirect");
      navigate("/register?next=/pricing");
      return;
    }
    if (!selected) return;
    setLoading(true);
    track("Upgrade Started", { props: { cadence } });
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: selected.lookup_key,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start checkout.");
    } finally {
      setLoading(false);
    }
  };

  const openPortal = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/payments/portal", { origin_url: window.location.origin });
      window.location.href = data.portal_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not open billing portal.");
    } finally {
      setLoading(false);
    }
  };

  const isPro = currentPlan?.plan === "pro";

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-16 fade-up">
      <div className="text-center max-w-2xl mx-auto">
        <p className="text-xs uppercase tracking-widest font-semibold" style={{ color: "#5C6B66" }}>
          Plans & pricing
        </p>
        <h1 className="mt-2 font-serif-display text-4xl sm:text-5xl font-semibold leading-tight" style={{ color: "#1C2826" }}>
          Simple pricing. <span style={{ color: "#2D5A4C" }}>Every regimen, examined.</span>
        </h1>
        <p className="mt-4 text-base" style={{ color: "#5C6B66" }}>
          Start free — upgrade to Harmonix Pro whenever you want the whole toolkit and the whole family under one plan.
        </p>
      </div>

      {/* Cadence toggle */}
      <div className="mt-8 flex justify-center">
        <div
          className="inline-flex rounded-full p-1"
          style={{ background: "#F4EFE6", border: "1px solid #E5DEC9" }}
        >
          <button
            data-testid="cadence-monthly-btn"
            onClick={() => setCadence("monthly")}
            className="rounded-full px-5 py-2 text-sm font-semibold transition-colors"
            style={{
              background: cadence === "monthly" ? "#FFFFFF" : "transparent",
              color: cadence === "monthly" ? "#1C2826" : "#5C6B66",
              boxShadow: cadence === "monthly" ? "0 4px 12px -4px rgba(0,0,0,0.08)" : "none",
            }}
          >
            Monthly
          </button>
          <button
            data-testid="cadence-yearly-btn"
            onClick={() => setCadence("yearly")}
            className="rounded-full px-5 py-2 text-sm font-semibold transition-colors"
            style={{
              background: cadence === "yearly" ? "#FFFFFF" : "transparent",
              color: cadence === "yearly" ? "#1C2826" : "#5C6B66",
              boxShadow: cadence === "yearly" ? "0 4px 12px -4px rgba(0,0,0,0.08)" : "none",
            }}
          >
            Yearly
            <span
              className="ml-2 text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full"
              style={{ background: "#FDF0EA", color: "#BF5832" }}
            >
              Save $18
            </span>
          </button>
        </div>
      </div>

      {/* Cards */}
      <div className="mt-10 grid md:grid-cols-2 gap-6">
        {/* Free */}
        <div className="card-warm p-8" data-testid="plan-card-free">
          <p className="text-xs uppercase tracking-widest font-semibold" style={{ color: "#5C6B66" }}>
            Harmonix Free
          </p>
          <h2 className="mt-2 font-serif-display text-3xl font-semibold" style={{ color: "#1C2826" }}>
            $0
            <span className="text-base font-normal ml-2" style={{ color: "#5C6B66" }}>
              forever
            </span>
          </h2>
          <p className="mt-2 text-sm" style={{ color: "#5C6B66" }}>
            For when you just want a quick, safe check on your current regimen.
          </p>

          <ul className="mt-6 space-y-3">
            {FEATURES_FREE.map((f, i) => (
              <li key={i} className="flex items-start gap-2 text-sm" style={{ color: "#1C2826" }}>
                <Check size={16} style={{ color: "#5C6B66" }} className="mt-0.5 shrink-0" />
                {f}
              </li>
            ))}
          </ul>

          <Button
            variant="outline"
            className="w-full mt-8 rounded-full h-12 font-semibold"
            disabled={!!user && !isPro}
            onClick={() => (user ? navigate("/dashboard") : navigate("/register"))}
            data-testid="free-cta-btn"
          >
            {user ? (isPro ? "You're on Pro" : "You're on Free") : "Start free"}
          </Button>
        </div>

        {/* Pro */}
        <div
          className="rounded-3xl p-8 relative"
          style={{
            background: "linear-gradient(160deg, #2D5A4C 0%, #23473C 100%)",
            color: "#FAF7F2",
            boxShadow: "0 20px 40px -10px rgba(45, 90, 76, 0.4)",
          }}
          data-testid="plan-card-pro"
        >
          <span
            className="absolute top-6 right-6 text-[10px] uppercase tracking-widest font-bold px-2.5 py-1 rounded-full"
            style={{ background: "#D96B43", color: "white" }}
          >
            Recommended
          </span>
          <p className="text-xs uppercase tracking-widest font-semibold" style={{ color: "#E8F0EC" }}>
            Harmonix Pro
          </p>
          <h2 className="mt-2 font-serif-display text-3xl font-semibold">
            {selected ? `$${(selected.amount / 100).toFixed(0)}` : "…"}
            <span className="text-base font-normal ml-2" style={{ color: "#E8F0EC" }}>
              /{cadence === "monthly" ? "month" : "year"}
            </span>
          </h2>
          {monthlyEquivalent && (
            <p className="text-xs mt-1" style={{ color: "#E8F0EC" }}>
              ≈ ${monthlyEquivalent}/month, billed yearly
            </p>
          )}
          <p className="mt-2 text-sm" style={{ color: "#E8F0EC" }}>
            The full toolkit — for you and everyone you care for.
          </p>

          <ul className="mt-6 space-y-3">
            {FEATURES_PRO.map((f, i) => (
              <li key={i} className="flex items-start gap-2 text-sm">
                <div
                  className="mt-0.5 w-5 h-5 rounded-full grid place-items-center shrink-0"
                  style={{ background: "#D96B43" }}
                >
                  <f.icon size={11} color="white" />
                </div>
                {f.text}
              </li>
            ))}
          </ul>

          <Button
            data-testid="pro-checkout-btn"
            onClick={isPro ? openPortal : startCheckout}
            disabled={loading}
            className="w-full mt-8 rounded-full h-12 font-semibold btn-terracotta"
          >
            {loading ? (
              <>
                <Loader2 size={16} className="animate-spin mr-2" /> Redirecting…
              </>
            ) : isPro ? (
              <>
                <Settings size={16} className="mr-2" /> Manage subscription
              </>
            ) : (
              <>
                Upgrade to Pro <ChevronRight size={16} className="ml-1" />
              </>
            )}
          </Button>

          <p className="mt-4 text-[11px] text-center" style={{ color: "#E8F0EC" }}>
            {isPro
              ? "Update card, download invoices, or cancel anytime — no email required."
              : "Secure checkout by Stripe · Cancel anytime"}
          </p>
        </div>
      </div>

      <p className="mt-8 text-center text-xs" style={{ color: "#5C6B66" }}>
        Test mode — use card <strong>4242 4242 4242 4242</strong>, any future expiry, any CVC.
      </p>
    </div>
  );
}
